import {
  Fragment,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import type { Route } from '../studies/routing.ts';
import { RouteLink } from '../studies/RouteLink.tsx';

/** Who is signed in and the controls over the session, shown at the end of every header menu. */
export interface HeaderAccount {
  email: string;
  /** Whether a sign-out is waiting on the server; the button says so and takes no second press. */
  signingOut: boolean;
  /** Why the last sign-out failed, or null. */
  signOutError: string | null;
  onSignOut: () => void;
  /** The app's own navigation, which the Account link goes through instead of a page load. */
  onNavigate: (route: Route) => void;
}

export interface HeaderMenuProps {
  /** Names the menu for a screen reader, since its summary shows only an ellipsis. */
  label: string;
  /** Controls listed above the account section: what this screen offers, if anything. */
  items?: ReactNode;
  account: HeaderAccount;
}

/**
 * Marks a control inside the menu that keeps it open once pressed: one that answers in place,
 * such as a delete that asks to be confirmed, or a sign-out whose failure is reported here.
 */
export const KEEPS_MENU_OPEN = { 'data-menu-keeps-open': '' } as const;

/**
 * The `…` menu at the end of every header: the screen's own items, then who is signed in with a
 * link to their account, then Sign out.
 *
 * A `<details>`, so it opens and closes without script and announces its state. Script adds what a
 * menu is expected to do besides: Escape closes it, a press outside closes it, focus moving to
 * something outside it closes it, and so does pressing a link or button inside it — unless that
 * control is marked `KEEPS_MENU_OPEN`, because it answers inside the menu. Closing from inside it
 * puts focus back on its summary, so focus is never left on a control nobody can see.
 *
 * It does not close while a sign-out is on its way, because a sign-out that fails says so here,
 * and a closed menu would say it to nobody. For the same reason a menu that appears while a
 * sign-out is on its way — on a screen its items or the Account link moved to — appears open.
 * Whatever its items were in the middle of — a delete
 * waiting to be confirmed — is dropped when it closes, so it opens again on the items as they
 * started.
 */
export const HeaderMenu = ({ label, items, account }: HeaderMenuProps) => {
  const menu = useRef<HTMLDetailsElement>(null);
  const summary = useRef<HTMLElement>(null);
  /** Bumped each time the menu closes; the items are keyed by it, which starts them afresh. */
  const [closings, setClosings] = useState(0);
  const signingOut = useRef(account.signingOut);
  signingOut.current = account.signingOut;

  const close = (returnFocus: boolean) => {
    const details = menu.current;
    if (details?.open !== true || signingOut.current) {
      return;
    }

    details.open = false;
    if (returnFocus) {
      summary.current?.focus();
    }
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  useLayoutEffect(() => {
    if (signingOut.current && menu.current !== null) {
      menu.current.open = true;
    }
  }, []);

  useEffect(() => {
    const closeOnPressOutside = (event: PointerEvent) => {
      if (menu.current?.contains(event.target as Node) === false) {
        closeRef.current(false);
      }
    };
    document.addEventListener('pointerdown', closeOnPressOutside);

    return () => document.removeEventListener('pointerdown', closeOnPressOutside);
  }, []);

  const closeOnActivation = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as Element;
    const control = target.closest('a, button');
    if (control === null || control.closest('[data-menu-keeps-open]') !== null) {
      return;
    }

    close(true);
  };

  return (
    <details
      ref={menu}
      className="header-menu"
      data-test-id="header-menu"
      onToggle={() => {
        if (menu.current?.open === false) {
          setClosings((count) => count + 1);
        }
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || menu.current?.open !== true) {
          return;
        }

        event.preventDefault();
        close(true);
      }}
      onBlur={(event) => {
        // Only focus that lands somewhere else closes it: focus that goes nowhere — a click on the
        // panel's text, a control replaced under it — is still in the menu as far as anyone can see.
        const next = event.relatedTarget;
        if (next instanceof Node && menu.current?.contains(next) === false) {
          close(false);
        }
      }}
    >
      <summary
        ref={summary}
        aria-label={label}
        data-test-id="header-menu-toggle"
        onClick={(event) => {
          if (signingOut.current && menu.current?.open === true) {
            event.preventDefault();
          }
        }}
      >
        ···
      </summary>
      <div
        className="header-menu-panel"
        data-test-id="header-menu-panel"
        onClick={closeOnActivation}
      >
        <Fragment key={closings}>{items}</Fragment>
        <div className="header-menu-account">
          <div className="header-menu-account-row">
            <span className="header-menu-email" data-test-id="signed-in-as">
              {account.email}
            </span>
            <RouteLink
              to={{ name: 'account' }}
              onNavigate={account.onNavigate}
              testId="account-link"
            >
              Account
            </RouteLink>
          </div>
          <button
            type="button"
            className="header-menu-sign-out"
            data-test-id="sign-out"
            disabled={account.signingOut}
            onClick={account.onSignOut}
            {...KEEPS_MENU_OPEN}
          >
            {account.signingOut ? 'Signing out…' : 'Sign out'}
          </button>
          {account.signOutError === null ? null : (
            <span className="chat-error" data-test-id="sign-out-error" role="alert">
              {account.signOutError}
            </span>
          )}
        </div>
      </div>
    </details>
  );
};
